import React, { useEffect, useState } from 'react';
import {
  Calendar,
  dayjsLocalizer,
  EventProps,
  DateLocalizer,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { styled } from 'styled-components';
import { Tooltip } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import DayDetailsCard from '../DayDetailsCard/DayDetailsCard';
import { getDiaryEntries } from '../../store/actions/general';
import {
  mapColorToEntrie,
  mapTextToEntrie,
  loadEvents,
  areDatesEqualWithoutTime,
} from '../../lib/helpers/diary';
import Loader from '../Loader/Loader';
import { MHColours } from '../../constants/theme';
import 'dayjs/locale/sv';

import { RootState } from '../../store/types/sharedTypes';
import type { AppDispatch } from '../../store';
import { ICalendarEvent, IDiaryEntry } from '../../lib/interfaces';

dayjs.locale('sv');

const localizer = dayjsLocalizer(dayjs);

export const EventCircle = styled.div<{ color?: string }>`
  height: 15px;
  width: 15px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  display: inline-block;
  margin: 0px 5px 5px 5px;
`;

export const CalendarContainer = styled.div`
  display: flex;
  width: 100%;
`;
export const DayDescription = styled.div`
  flex: 1;
`;

const MigrainesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;
const MigraineContainer = styled.div`
  display: inline-block;
  white-space: nowrap;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- without the cast the styled Calendar's props reject our typed handlers (TS2769)
const StyledCalendar = styled(Calendar as any)`
  .rbc-toolbar-label {
    padding-right: 240px;
  }

  .rbc-month-header {
    .rbc-header {
      padding: 10px;
    }
  }

  .rbc-day-bg + .rbc-day-bg {
    border: none;
  }
`;

interface MigraineCalendarProps {
  jumpToDate: Dayjs;
}

const MigraineCalendar = (props: MigraineCalendarProps) => {
  const { t } = useTranslation();

  const [selectedDay, setSelectedDay] = useState<{
    start: Date;
    end?: Date;
  } | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  const { patientDetailsLoading, calendar, patientDetails } = useSelector(
    (state: RootState) => state.general
  );

  const dispatch = useDispatch<AppDispatch>();

  const [calendarStartDate, setCalendarStartDate] = useState<Date>(new Date());
  const [calendarEvents, setCalendarEvents] = useState<ICalendarEvent[]>([]);
  const [selectedDayEntry, setSelectedDayEntry] = useState<IDiaryEntry | null>(
    null
  );

  useEffect(() => {
    setSelectedMonth(props.jumpToDate.month());
    setCalendarStartDate(props.jumpToDate.toDate());
  }, [props.jumpToDate]);

  useEffect(() => {
    setCalendarEvents(loadEvents(calendar.diaryEntries || []));
  }, [calendar.diaryEntries]);

  const handleDaySelect = (
    event: { start: Date; end?: Date } | ICalendarEvent
  ) => {
    const clickedDayEntry = (calendar.diaryEntries || []).find(
      (entry: IDiaryEntry) =>
        areDatesEqualWithoutTime(entry.diaryDate, event.start.toISOString())
    );
    if (clickedDayEntry) {
      setSelectedDay(event);
      setSelectedDayEntry(clickedDayEntry);
    } else {
      setSelectedDay(null);
      setSelectedDayEntry(null);
    }
  };

  let counterId = 0;

  // eslint-disable-next-line react/no-unstable-nested-components -- keys depend on the per-render counterId closure; hoisting would change keys and remount behaviour
  const EventComponent: React.FC<EventProps<ICalendarEvent>> = ({ event }) =>
    event?.entries?.map((entrie: string, index: number) => {
      const key = `${event.id}-${entrie}-${counterId}`;
      counterId += 1;
      return (
        <div style={{ width: '25px', display: 'inline' }} key={key}>
          {index === 5 && <br />}
          <Tooltip title={mapTextToEntrie(entrie)}>
            <EventCircle color={mapColorToEntrie(entrie)} />
          </Tooltip>
        </div>
      );
    });

  const eventStyleGetter = (event: ICalendarEvent, start: Date) => {
    const isInSelectedMonth = selectedMonth === dayjs(start).month();
    let color = 'white';
    if (!isInSelectedMonth) {
      color = '#e6e6e6';
    }

    if (dayjs(event.start).isSame(dayjs(), 'day')) {
      color = '#eaf6ff';
    }
    const style = {
      backgroundColor: color,
      borderRadius: '5px',
      opacity: 1,
      color: 'black',
      border: '0px',
      display: 'block',
      paddingTop: '10px',
      marginTop: event.entries && event.entries.length > 5 ? '0px' : '20px',
    };
    return {
      style,
    };
  };

  const onCalendarNavigate = (newDate: Date) => {
    setSelectedMonth(dayjs(newDate).month());
    setCalendarStartDate(newDate);
  };

  const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
    const start = Array.isArray(range) ? range[0] : range.start;
    const end = Array.isArray(range) ? range[range.length - 1] : range.end;

    if (patientDetails?._id && start && end) {
      dispatch(
        getDiaryEntries(
          start.toISOString(),
          end.toISOString(),
          patientDetails._id
        )
      );
    }
  };

  const formats = {
    weekdayFormat: (
      date: Date,
      culture?: string,
      dateLocalizer?: DateLocalizer
    ) => (dateLocalizer ? dateLocalizer.format(date, 'dddd', culture) : ''),
    monthHeaderFormat: (
      date: Date,
      culture?: string,
      dateLocalizer?: DateLocalizer
    ) => {
      if (!dateLocalizer) return '';
      const formattedDate = dateLocalizer.format(date, 'MMMM YYYY', culture);
      return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    },
  };

  return (
    <CalendarContainer style={{ minHeight: '500px', marginTop: '20px' }}>
      {patientDetailsLoading ? (
        <Loader />
      ) : (
        <>
          {selectedDay && selectedDayEntry && (
            <DayDescription>
              <DayDetailsCard dayEntry={selectedDayEntry} />
            </DayDescription>
          )}
          <div style={{ flex: selectedDay ? 4 : 5, minWidth: 0 }}>
            <StyledCalendar
              date={calendarStartDate}
              style={{ cursor: 'pointer', height: 500 }}
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              views={['month']}
              eventPropGetter={eventStyleGetter}
              components={{
                event: EventComponent,
              }}
              selectable
              onSelectSlot={(slotInfo: { start: Date; end: Date }) =>
                handleDaySelect(slotInfo)
              }
              onDrillDown={(date: Date) => handleDaySelect({ start: date })}
              onSelectEvent={(event: ICalendarEvent) => handleDaySelect(event)}
              onNavigate={onCalendarNavigate}
              onRangeChange={handleRangeChange}
              messages={{
                next: t('next') as string,
                today: t('today') as string,
                previous: t('previous') as string,
              }}
              formats={formats}
            />
            <MigrainesContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigraineRed}
                />{' '}
                {t('headache')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigraineBlue}
                />{' '}
                {t('migrain')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigrainePurpleLight}
                />{' '}
                {t('tensionHeadache')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigraineYellow}
                />{' '}
                {t('horton')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigraineOrange}
                />{' '}
                {t('otherHeadache')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigrainePink}
                />{' '}
                {t('aura')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigrainePurple}
                />{' '}
                {t('auraWithoutHeadache')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigraineGreenLight}
                />{' '}
                {t('noHeadache')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigraineGreenFluorescent}
                />{' '}
                {t('medication')}
              </MigraineContainer>
              <MigraineContainer>
                <EventCircle
                  style={{ margin: '12px 0 0 12px ' }}
                  color={MHColours.mhMigraineSeeBlue}
                />{' '}
                {t('menstruation')}
              </MigraineContainer>
            </MigrainesContainer>
          </div>
        </>
      )}
    </CalendarContainer>
  );
};

export default MigraineCalendar;
